<template>
  <footer
    class="relative flex flex-col break-words pb-36 pt-20 sm:pt-24 lg:pb-12 lg:pt-20"
  >
    <div
      class="container relative order-2 mx-auto mb-14 px-6 sm:mb-20 sm:px-8 lg:order-none"
    >
      <ul class="hidden flex-col items-start gap-4 lg:flex lg:flex-row">
        <li
          v-for="group in navGroupList"
          :key="group.key"
          class="flex w-auto gap-6 lg:w-1/3"
        >
          <IntlMsg
            v-bind="{ k: group.key }"
            class="min-w-24 text-xl font-semibold leading-6"
          />

          <nav v-if="group.children" class="lg:mt-1">
            <AppLink
              v-for="link in group.children"
              :key="link.key"
              class="mb-1.5 block transition-colors last:mb-0 hover:text-blue-700"
              v-bind="{ to: link.to }"
            >
              <IntlMsg v-bind="{ k: link.key }" />
            </AppLink>
          </nav>
        </li>
      </ul>

      <AccordionLinks
        v-bind="{
          navGroupList,
          openedKeys: ['menu.services'],
        }"
        class="lg:hidden"
      />
    </div>

    <div
      role="presentation"
      class="relative -z-1 order-1 mb-4 flex items-center justify-center sm:mb-8 lg:order-none lg:mb-0"
    >
      <div class="c-w-container mx-auto w-full max-w-9xl">
        <AppLogoFooter class="relative h-full w-full text-gray-250" />
        <div
          class="absolute inset-0 -bottom-12 -left-16 bg-gradient-to-t from-gray-100"
        ></div>
      </div>
    </div>

    <div
      class="container relative order-3 mx-auto flex flex-col gap-14 px-6 sm:gap-16 sm:px-8 lg:order-none lg:-mt-16 lg:grid lg:grid-cols-2 lg:gap-6"
    >
      <div
        class="flex w-auto flex-col items-start gap-5 lg:grid lg:grid-cols-3 lg:gap-0"
      >
        <div class="hidden border-r-2 pr-5 lg:block">
          <IntlMsg
            v-bind="{ k: 'footer.we-accept' }"
            class="mb-4 block text-xl font-semibold leading-6 tracking-tight"
          />
          <ul class="flex flex-wrap items-center gap-x-4 gap-y-2">
            <li
              v-for="paymentSystem in paymentSystems"
              :key="paymentSystem.label"
              class="opacity-50 transition-opacity hover:opacity-80"
            >
              <img
                :src="paymentSystem.icon"
                :alt="paymentSystem.label"
                loading="lazy"
                class="h-8"
              />
            </li>
          </ul>
        </div>

        <div
          class="grid grid-cols-3 items-start justify-items-start gap-x-9 gap-y-5 text-lg leading-6 sm:items-center sm:gap-5 lg:col-span-2 lg:pl-6 lg:text-base lg:leading-5"
        >
          <div
            class="text-xl font-semibold leading-7 sm:text-2xl sm:leading-5 lg:hidden"
          >
            <IntlMsg v-bind="{ k: 'footer.payment' }" />
          </div>
          <ul
            class="col-span-2 flex flex-wrap items-center gap-x-4 gap-y-2 lg:hidden"
          >
            <li
              v-for="paymentSystem in paymentSystems"
              :key="paymentSystem.label"
              class="opacity-50 transition-opacity hover:opacity-80"
            >
              <img
                :src="paymentSystem.icon"
                :alt="paymentSystem.label"
                loading="lazy"
                class="aspect-auto h-9"
              />
            </li>
          </ul>

          <IntlMsg
            v-bind="{ k: 'footer.preferred-language' }"
            class="hidden lg:inline"
          />
          <IntlMsg
            v-bind="{ k: 'footer.language' }"
            class="text-xl font-semibold leading-7 sm:text-2xl sm:leading-5 lg:hidden"
          />
          <LocaleSelect v-bind="{ size: 'sm' }" class="col-span-2 xl:mb-0" />

          <IntlMsg
            v-bind="{ k: 'footer.currency-display' }"
            class="hidden lg:inline"
          />
          <IntlMsg
            v-bind="{ k: 'footer.currency' }"
            class="self-center text-xl font-semibold leading-7 sm:self-auto sm:text-2xl sm:leading-5 lg:hidden"
          />
          <CurrencySelectDropdown class="hidden lg:flex" />
          <CurrencySelect
            v-bind="{ hasLabel: false }"
            class="col-span-2 lg:hidden"
          />
        </div>
      </div>

      <nav
        class="flex flex-col flex-wrap gap-x-9 gap-y-5 sm:flex-row lg:border-l-2 lg:pl-6"
      >
        <AppLink
          v-for="link in etcLinks"
          :key="link.key"
          v-bind="{ to: link.to }"
          class="leading-6 underline decoration-gray-300 decoration-1 underline-offset-8 transition-colors hover:decoration-blue-700 active:decoration-blue-500"
        >
          <IntlMsg v-bind="{ k: link.key }" />
        </AppLink>
      </nav>
    </div>

    <div class="ios-safe-inset-bottom h-0.5"></div>
  </footer>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import AccordionLinks from '/@/modules/app/accordion/AccordionLinks.vue'

import { IntlMsg, type AppNavItem, type AppNavGroup } from '/@shared/app'
import {
  AppLink,
  LocaleSelect,
  CurrencySelect,
  CurrencySelectDropdown,
  AppLogoFooter,
  useLocaleFeatures,
} from '/@app'

import mastercardSrc from '/@/assets/images/logos/mastercard-footer.svg?url'
import mirSrc from '/@/assets/images/logos/mir-footer.svg?url'
import paypalSrc from '/@/assets/images/logos/paypal-footer.svg?url'
import sbpSrc from '/@/assets/images/logos/sbp-footer.svg?url'
import visaSrc from '/@/assets/images/logos/visa-footer.svg?url'

// import bgEllipse from '/@/assets/images/dark/1.png'
// import bgEllipse2 from '/@/assets/images/dark/2.png'
// import bgEllipse3 from '/@/assets/images/dark/3.png'
// import bgEllipse4 from '/@/assets/images/dark/4.png'

const { isVpnAvailable, isRuUser } = useLocaleFeatures()

const navGroupList = computed(() => {
  const list: AppNavGroup[] = [
    {
      key: 'menu.services',
      children: [
        {
          to: { name: 'DsPage' },
          key: 'footer.menu.dedicated',
        },
        {
          to: { name: 'VpsPage' },
          key: 'footer.menu.vps',
        },
        {
          to: { name: 'VhPage' },
          key: 'footer.menu.web-hosting',
        },
        {
          to: { name: 'BackupPage' },
          key: 'footer.menu.backups',
        },
      ],
    },
  ]

  if (isVpnAvailable.value)
    list[0].children.push({
      to: { name: 'VpnPage' },
      key: 'footer.menu.vpn',
    })

  list.push(
    {
      key: 'menu.resources',
      children: [
        {
          to: { name: 'HelpIndexPage' },
          key: 'footer.menu.help',
        },
        {
          to: { name: 'HelpArticlePage', params: { slug: 'partner-program' } },
          key: 'footer.menu.partner-program',
        },
        {
          to: { name: 'ApiPage' },
          key: 'footer.menu.api',
        },
        {
          to: 'https://x.com/fornex_com',
          key: 'socials.twitter',
        },
        {
          to: 'https://t.me/fornex_hosting',
          key: 'socials.telegram-channel',
        },
      ],
    },
    {
      key: 'menu.company',
      children: [
        {
          to: { name: 'NewsPage' },
          key: 'footer.menu.news',
        },
        {
          to: { name: 'ServicesPage' },
          key: 'footer.menu.our-services',
        },
        {
          to: {
            name: 'HomePage',
            hash: '#ourclients',
          },
          key: 'footer.menu.our-customers',
        },
        {
          to: { name: 'ContactsPage' },
          key: 'footer.menu.contacts',
        },
      ],
    }
  )

  return list
})

const paymentSystems = computed(() => {
  const list: {
    label: string
    icon: string
    // url: string
  }[] = [
    {
      label: 'Visa',
      icon: visaSrc,
      // url: 'https://cis.visa.com/',
    },
    {
      label: 'MasterCard',
      icon: mastercardSrc,
      // url: 'https://www.mastercard.com/',
    },
    {
      label: 'PayPal',
      icon: paypalSrc,
      // url: 'https://www.paypal.com/',
    },
  ]

  if (isRuUser.value)
    list.push(
      {
        label: 'СБП',
        icon: sbpSrc,
        // url: 'https://sbp.nspk.ru/',
      },
      {
        label: 'МИР',
        icon: mirSrc,
        // url: 'https://www.nspk.ru/',
      }
    )

  return list
})

const etcLinks = computed(() => {
  const list: AppNavItem[] = [
    {
      to: { name: 'TosPage' },
      key: 'menu.terms-and-conditions',
    },
    {
      to: { name: 'GdprPage' },
      key: 'menu.gdpr',
    },
    {
      to: { name: 'PrivacyPage' },
      key: 'menu.privacy-policy',
    },
    {
      to: { name: 'HelpArticlePage', params: { slug: 'test-period' } },
      key: 'menu.trial',
    },
    {
      to: { name: 'HelpArticlePage', params: { slug: 'moneyback' } },
      key: 'menu.money-refund',
    },
  ]

  if (isRuUser.value) {
    list.push({
      to: { name: 'PersonalDataProcessingPolicyPage' },
      key: 'menu.personal-data-processing-policy',
    })
  }

  return list
})
</script>
